import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Download as DownloadIcon,
  Share as ShareIcon,
  Print as PrintIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AccessTime as ClockIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { api } from '../services/api';
import ThemeToggle from '../components/ui/ThemeToggle';
import useSettingsStore from '../stores/settingsStore';
import { getPDFRenderStyles, getFontImports } from '../styles/pdfRenderStyles';
import { splitPDFContent } from '../utils/pdfPageSplitter';

// API base URL for public endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

const PDFPublicView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id, shortCode, slug } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { initSettings } = useSettingsStore();
  
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [content, setContent] = useState('');
  const [customCSS, setCustomCSS] = useState('');
  const [customJS, setCustomJS] = useState('');
  const [scrollDepths, setScrollDepths] = useState(new Set());
  const [startTime, setStartTime] = useState(null);
  const [lastActiveTime, setLastActiveTime] = useState(null);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(true);
  
  // Determine URL type and identifier
  const getIdentifier = () => {
    if (id) return { type: 'id', value: id };
    if (shortCode) return { type: 'shortCode', value: shortCode };
    if (slug) return { type: 'slug', value: slug };
    return null;
  };
  
  // Fetch PDF data
  const fetchPDF = async (providedPassword = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const identifier = getIdentifier();
      if (!identifier) {
        throw new Error('Invalid URL');
      }
      
      let url;
      switch (identifier.type) {
        case 'id':
          url = `/pdf/view/${identifier.value}`;
          break;
        case 'shortCode':
          url = `/p/${identifier.value}`;
          break;
        case 'slug':
          url = `/docs/${identifier.value}`;
          break;
      }
      
      // Preserve existing URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      
      // Add or update password parameter if provided
      if (providedPassword) {
        urlParams.set('password', providedPassword);
      }
      
      const params = urlParams.toString() ? `?${urlParams.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}${url}${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.log('Response not OK:', response.status, data);
        if (response.status === 401 && data.data?.requiresPassword) {
          console.log('Password required, showing password prompt');
          setPasswordRequired(true);
          setPdf({ title: data.data.title });
          setLoading(false);
          return;
        }
        const error = new Error(data.message || 'Failed to load PDF');
        error.status = response.status;
        throw error;
      }
      
      setPdf(data.data.pdf);
      setContent(data.data.pdf.content);
      setCustomCSS(data.data.pdf.customCSS || '');
      setCustomJS(data.data.pdf.customJS || '');
      setPasswordRequired(false);
      setPasswordError('');
      
      // Initialize time tracking
      setStartTime(Date.now());
      setLastActiveTime(Date.now());
      
      // Track view event
      trackEvent('view');
    } catch (err) {
      console.error('Error fetching PDF:', err);
      
      if (err.status === 401 || err.message === 'Invalid password') {
        // Stay on password screen for 401 errors
        setPasswordRequired(true);
        setPasswordError(err.message || 'Invalid password');
        // Don't set general error for password issues
        // Keep the PDF title if we have it
        if (!pdf) {
          setPdf({ title: 'Protected PDF' });
        }
      } else if (err.status === 404) {
        setError('PDF not found');
      } else if (err.status === 410) {
        setError('This PDF has expired');
      } else {
        setError(err.message || 'Failed to load PDF');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Track analytics event
  const trackEvent = async (event, data = {}) => {
    try {
      const identifier = getIdentifier();
      if (!identifier || !pdf?._id) return;
      
      await fetch(`${API_BASE_URL}/api/pdfs/${pdf._id}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event,
          eventData: data,
          sessionId: localStorage.getItem('pdfSessionId') || generateSessionId()
        })
      });
    } catch (err) {
      console.error('Failed to track event:', err);
    }
  };
  
  // Generate session ID
  const generateSessionId = () => {
    const sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem('pdfSessionId', sessionId);
    return sessionId;
  };
  
  // Handle password submit
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }
    // Clear any previous errors
    setPasswordError('');
    setError(null);
    fetchPDF(password);
  };
  
  // Handle download
  const handleDownload = async () => {
    try {
      if (!pdf?.currentPdfUrl) {
        // If no PDF URL, just download the view
        window.print();
      } else {
        // Use the download endpoint
        const downloadUrl = `${API_BASE_URL}/pdf/download/${pdf._id}`;
        window.open(downloadUrl, '_blank');
      }
      trackEvent('download');
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  
  // Handle print
  const handlePrint = () => {
    window.print();
    trackEvent('print');
  };
  
  // Handle share
  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: pdf?.title || 'PDF Document',
          url
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(url);
      // TODO: Show notification
    }
    
    trackEvent('share');
  };
  
  // Handle scroll tracking
  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / documentHeight) * 100);
    
    // Track scroll milestones
    const milestones = [25, 50, 75, 100];
    milestones.forEach(milestone => {
      if (scrollPercent >= milestone && !scrollDepths.has(milestone)) {
        setScrollDepths(prev => new Set([...prev, milestone]));
        trackEvent('scroll', { scrollDepth: milestone });
      }
    });
  };
  
  // Handle click tracking
  const handleClick = (e) => {
    const target = e.target;
    const clickData = {
      clickTarget: target.tagName,
      clickText: target.textContent?.substring(0, 100) || '',
      elementId: target.id || '',
      className: target.className || '',
      href: target.href || '',
      coordinates: {
        x: e.clientX,
        y: e.clientY
      }
    };
    
    trackEvent('click', clickData);
  };
  
  // Time tracking functions
  const updateActiveTime = () => {
    const now = Date.now();
    setLastActiveTime(now);
  };
  
  const handleVisibilityChange = () => {
    const isVisible = !document.hidden;
    setIsPageVisible(isVisible);
    
    if (isVisible) {
      setLastActiveTime(Date.now());
    } else {
      // Track time spent when page becomes hidden
      if (startTime && lastActiveTime) {
        const sessionTime = Math.round((Date.now() - startTime) / 1000);
        trackEvent('time_spent', { duration: sessionTime });
        setTotalTimeSpent(prev => prev + sessionTime);
      }
    }
  };
  
  // Track time spent on page unload
  const handleBeforeUnload = () => {
    if (startTime) {
      const sessionTime = Math.round((Date.now() - startTime) / 1000);
      trackEvent('time_spent', { duration: sessionTime });
    }
  };
  
  // Initial load
  useEffect(() => {
    // Initialize settings for theme support
    initSettings();
    fetchPDF();
  }, [id, shortCode, slug]);

  // Set up scroll tracking
  useEffect(() => {
    if (pdf && !passwordRequired && !error) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [pdf, passwordRequired, error, scrollDepths]);

  // Set up click and time tracking
  useEffect(() => {
    if (pdf && !passwordRequired && !error) {
      // Add click tracking to PDF content area
      const contentArea = document.querySelector('.pdf-content-wrapper');
      if (contentArea) {
        contentArea.addEventListener('click', handleClick, { passive: true });
      }
      
      // Add activity tracking (mouse movement, keyboard, scroll)
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      activityEvents.forEach(event => {
        document.addEventListener(event, updateActiveTime, { passive: true });
      });
      
      // Add visibility change tracking
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Add beforeunload tracking
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // Set up periodic time tracking (every 30 seconds if active)
      const timeInterval = setInterval(() => {
        if (isPageVisible && startTime && lastActiveTime) {
          const timeSinceLastActive = Date.now() - lastActiveTime;
          // Only track if user was active in the last 30 seconds
          if (timeSinceLastActive < 30000) {
            const sessionTime = Math.round((Date.now() - startTime) / 1000);
            if (sessionTime > 30) { // Only track sessions longer than 30 seconds
              trackEvent('time_spent', { duration: sessionTime });
              setStartTime(Date.now()); // Reset timer
            }
          }
        }
      }, 30000);
      
      return () => {
        if (contentArea) {
          contentArea.removeEventListener('click', handleClick);
        }
        activityEvents.forEach(event => {
          document.removeEventListener(event, updateActiveTime);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        clearInterval(timeInterval);
      };
    }
  }, [pdf, passwordRequired, error, isPageVisible, startTime, lastActiveTime]);

  // Set document title and meta description
  useEffect(() => {
    if (pdf) {
      document.title = pdf.title || 'PDF Document';
      
      // Set or remove meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (pdf.config?.seo?.metaDescription) {
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          document.head.appendChild(metaDescription);
        }
        metaDescription.content = pdf.config.seo.metaDescription;
      } else if (metaDescription) {
        document.head.removeChild(metaDescription);
      }
    }
  }, [pdf]);
  
  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3
      }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', position: 'relative' }}>
          {/* Theme toggle in top right corner */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <ThemeToggle size="small" />
          </Box>
          
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = 'http://aghrba.com'}
            sx={{ mt: 2 }}
          >
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // Password protection
  if (passwordRequired && (!pdf || !pdf.content)) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3
      }}>
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%', position: 'relative' }}>
          {/* Theme toggle in top right corner */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <ThemeToggle size="small" />
          </Box>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Password Protected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This PDF requires a password to view
            </Typography>
          </Box>
          
          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Clear error when user starts typing
                if (passwordError) setPasswordError('');
              }}
              error={!!passwordError}
              helperText={passwordError}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
            >
              View PDF
            </Button>
            
            {passwordError && (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => window.location.href = 'http://aghrba.com'}
                sx={{ mt: 1 }}
              >
                Go to Home
              </Button>
            )}
          </form>
        </Paper>
      </Box>
    );
  }
  
  // Main PDF view
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      color: 'text.primary'
    }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flex: 1, color: 'text.primary' }} noWrap>
            {pdf?.title || 'Untitled Document'}
          </Typography>
          
          {!isMobile && pdf?.updatedAt && (
            <Chip
              icon={<ClockIcon />}
              label={`Updated ${format(new Date(pdf.updatedAt), 'MMM d, yyyy')}`}
              size="small"
              sx={{ mr: 2 }}
            />
          )}
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* {pdf?.allowDownload !== false && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                variant="outlined"
                size="small"
              >
                Download
              </Button>
            )} */}
            
            <IconButton onClick={handlePrint} title="Print">
              <PrintIcon />
            </IconButton>
            
            <IconButton onClick={handleShare} title="Share">
              <ShareIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            
            <ThemeToggle />
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        
        {/* Tags */}
        {pdf?.tags?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {pdf.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}
        
        <Divider sx={{ mb: 4 }} />
        
        {/* PDF Content */}
        <Paper sx={{ 
          p: { xs: 2, md: 4 },
          maxWidth: 'none',
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[1],
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          {/* Watermark */}
          {pdf?.config?.watermark?.enabled && !pdf?.config?.watermark?.downloadOnly && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              {pdf?.config?.watermark?.pattern?.enabled ? (
                // Pattern mode - repeat watermark
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${Math.max(pdf.config.watermark.image ? (pdf.config.watermark.imageScale || 0.5) * 200 : pdf.config.watermark.textSize || 60, 50)}px, max-content))`,
                    gap: `${pdf.config.watermark.pattern?.spacing?.vertical || 150}px ${pdf.config.watermark.pattern?.spacing?.horizontal || 150}px`,
                    justifyContent: 'center',
                    alignContent: 'center',
                    overflow: 'hidden',
                    padding: '20px'
                  }}
                >
                  {(() => {
                    const density = pdf.config.watermark.pattern?.density || 'medium';
                    const densityMap = { low: 25, medium: 50, high: 75, maximum: 100 };
                    const count = densityMap[density];
                    return Array.from({ length: count }).map((_, index) => (
                    pdf?.config?.watermark?.image ? (
                      <Box
                        key={index}
                        component="img"
                        src={pdf.config.watermark.image}
                        alt="Watermark"
                        sx={{
                          opacity: pdf.config.watermark.opacity || 0.3,
                          width: `${(pdf.config.watermark.imageScale || 0.5) * 200}px`,
                          height: 'auto',
                          transform: `rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`,
                          margin: 0,
                          mixBlendMode: pdf.config.watermark.blendMode || 'normal',
                          userSelect: 'none'
                        }}
                      />
                    ) : (
                      <Box
                        key={index}
                        sx={{
                          fontSize: `${pdf.config.watermark.textSize || 60}px`,
                          fontWeight: pdf.config.watermark.fontStyle?.bold ? 'bold' : 'normal',
                          fontStyle: pdf.config.watermark.fontStyle?.italic ? 'italic' : 'normal',
                          fontFamily: `${pdf.config.watermark.fontFamily || 'Arial'}, sans-serif`,
                          color: pdf.config.watermark.color || 'rgba(0, 0, 0, 0.3)',
                          opacity: pdf.config.watermark.opacity || 0.3,
                          transform: `rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`,
                          margin: 0,
                          textTransform: pdf.config.watermark.textTransform || 'none',
                          mixBlendMode: pdf.config.watermark.blendMode || 'normal',
                          userSelect: 'none',
                          ...(pdf.config.watermark.border?.enabled && {
                            textShadow: `
                              -${pdf.config.watermark.border.width}px -${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},  
                               ${pdf.config.watermark.border.width}px -${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},
                              -${pdf.config.watermark.border.width}px  ${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},
                               ${pdf.config.watermark.border.width}px  ${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color}
                            `
                          })
                        }}
                      >
                        {pdf.config.watermark.text || 'CONFIDENTIAL'}
                      </Box>
                    )
                  ));
                  })()}
                </Box>
              ) : (
                // Single watermark
                pdf?.config?.watermark?.image ? (
                  <Box
                    component="img"
                    src={pdf.config.watermark.image}
                    alt="Watermark"
                    sx={{
                      position: 'absolute',
                      opacity: pdf.config.watermark.opacity || 0.3,
                      zIndex: 10,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      width: `${(pdf.config.watermark.imageScale || 0.5) * 100}%`,
                      maxWidth: `${(pdf.config.watermark.imageScale || 0.5) * 100}%`,
                      height: 'auto',
                      mixBlendMode: pdf.config.watermark.blendMode || 'normal',
                      ...(pdf.config.watermark.position === 'center' && {
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'diagonal' && {
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'top-left' && {
                        top: '20px',
                        left: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'top-right' && {
                        top: '20px',
                        right: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'bottom-left' && {
                        bottom: '20px',
                        left: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'bottom-right' && {
                        bottom: '20px',
                        right: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'custom' && {
                        top: `${pdf.config.watermark.customPosition?.y || 50}%`,
                        left: `${pdf.config.watermark.customPosition?.x || 50}%`,
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation || 0}deg)`
                      })
                    }}
                  />
                ) : pdf?.config?.watermark?.text && (
                  <Box
                    sx={{
                      position: 'absolute',
                      opacity: pdf.config.watermark.opacity || 0.3,
                      fontSize: `${pdf.config.watermark.textSize || 60}px`,
                      fontWeight: pdf.config.watermark.fontStyle?.bold ? 'bold' : 'normal',
                      fontStyle: pdf.config.watermark.fontStyle?.italic ? 'italic' : 'normal',
                      fontFamily: `${pdf.config.watermark.fontFamily || 'Arial'}, sans-serif`,
                      color: pdf.config.watermark.color || 'rgba(0, 0, 0, 0.3)',
                      zIndex: 10,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      textTransform: pdf.config.watermark.textTransform || 'none',
                      mixBlendMode: pdf.config.watermark.blendMode || 'normal',
                      ...(pdf.config.watermark.border?.enabled && {
                        textShadow: `
                          -${pdf.config.watermark.border.width}px -${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},  
                           ${pdf.config.watermark.border.width}px -${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},
                          -${pdf.config.watermark.border.width}px  ${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color},
                           ${pdf.config.watermark.border.width}px  ${pdf.config.watermark.border.width}px 0 ${pdf.config.watermark.border.color}
                        `
                      }),
                      ...(pdf.config.watermark.position === 'center' && {
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'diagonal' && {
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation !== undefined ? pdf.config.watermark.rotation : 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'top-left' && {
                        top: '20px',
                        left: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'top-right' && {
                        top: '20px',
                        right: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'bottom-left' && {
                        bottom: '20px',
                        left: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'bottom-right' && {
                        bottom: '20px',
                        right: '20px',
                        transform: `rotate(${pdf.config.watermark.rotation || 0}deg)`
                      }),
                      ...(pdf.config.watermark.position === 'custom' && {
                        top: `${pdf.config.watermark.customPosition?.y || 50}%`,
                        left: `${pdf.config.watermark.customPosition?.x || 50}%`,
                        transform: `translate(-50%, -50%) rotate(${pdf.config.watermark.rotation || 0}deg)`
                      })
                    }}
                  >
                    {pdf.config.watermark.text}
                  </Box>
                )
              )}
            </Box>
          )}
          
          {/* Import Google Fonts for proper font family support */}
          <div dangerouslySetInnerHTML={{ __html: getFontImports() }} />
          
          {/* Apply shared PDF render styles + custom CSS + page breaking styles */}
          <style dangerouslySetInnerHTML={{ 
            __html: `
              ${getPDFRenderStyles(customCSS)}
              
              /* Page break styles for proper page separation */
              .pdf-page {
                background: white;
                color: #000000;
                margin: 0 auto 40px;
                padding: ${pdf?.config?.margins?.top || 20}mm ${pdf?.config?.margins?.right || 20}mm ${pdf?.config?.margins?.bottom || 20}mm ${pdf?.config?.margins?.left || 20}mm;
                box-shadow: 0 4px 20px rgba(0,0,0,0.12);
                position: relative;
                min-height: 297mm;
                page-break-after: always;
                break-after: page;
                border: 1px solid #ddd;
                box-sizing: border-box;
                width: 100% !important;
                overflow-x: auto;
                overflow-y: visible;
                line-height: 1.7;
                font-size: 16px;
              }
              
              .pdf-page:last-child {
                margin-bottom: 0;
                page-break-after: avoid;
              }
              
              /* Default PDF content styles - can be overridden by custom CSS */
              .pdf-page h1, .pdf-page h2, .pdf-page h3, 
              .pdf-page h4, .pdf-page h5, .pdf-page h6 {
                color: #000000;
                font-weight: bold;
                margin-top: 1.5em;
                margin-bottom: 0.75em;
                line-height: 1.3;
              }
              
              .pdf-page h1 { font-size: 2.5rem; }
              .pdf-page h2 { font-size: 2rem; }
              .pdf-page h3 { font-size: 1.5rem; }
              .pdf-page h4 { font-size: 1.25rem; }
              .pdf-page h5 { font-size: 1.1rem; }
              .pdf-page h6 { font-size: 1rem; }
              
              .pdf-page p {
                margin-bottom: 1em;
                color: #000000;
              }
              
              .pdf-page ul, .pdf-page ol {
                padding-left: 2em;
                margin-bottom: 1em;
              }
              
              .pdf-page li {
                margin-bottom: 0.5em;
                color: #000000;
              }
              
              .pdf-page img {
                max-width: 100%;
                height: auto;
              }
              
              .pdf-page table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 1em;
              }
              
              .pdf-page table th,
              .pdf-page table td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              
              .pdf-page table th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              
              .pdf-page table td {
                background-color: white;
              }
              
              /* Hide page break markers in rendered content */
              .page-break, .ql-pagebreak {
                display: none !important;
              }
              
              @media print {
                .pdf-page {
                  margin: 0 !important;
                  box-shadow: none !important;
                  border: none !important;
                  page-break-after: always;
                }
                
                .pdf-page:last-child {
                  page-break-after: avoid;
                }
              }
            `
          }} />
          
          {/* Render content split into pages */}
          <Box className="pdf-content-wrapper">
            {(() => {
              const pages = splitPDFContent(content);
              return pages.map((pageContent, index) => (
                <Box
                  key={index}
                  className="pdf-page"
                  dangerouslySetInnerHTML={{ __html: pageContent }}
                  // Remove all sx styling - let CSS handle everything
                />
              ));
            })()}
          </Box>
        </Paper>
        
        {/* Apply custom JS if enabled */}
        {customJS && pdf?.config?.jsEnabled && (
          <script dangerouslySetInnerHTML={{ __html: customJS }} />
        )}
        
        {/* Footer */}
        {pdf?.config?.branding?.showLogo && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Powered by AghrbaSystem
            </Typography>
          </Box>
        )}
      </Container>
      
      {/* Print styles */}
      <style>
        {`
          @media print {
            .MuiAppBar-root,
            .MuiChip-root,
            .MuiDivider-root {
              display: none !important;
            }
            
            .MuiContainer-root {
              max-width: 100% !important;
              padding: 0 !important;
            }
            
            .MuiPaper-root {
              box-shadow: none !important;
              padding: 20px !important;
              border: none !important;
              background: white !important;
            }
            
            .pdf-content-wrapper * {
              color: black !important;
            }
            
            .pdf-content-wrapper h1, .pdf-content-wrapper h2, .pdf-content-wrapper h3 {
              color: #333 !important;
            }
            
            .pdf-content-wrapper code, .pdf-content-wrapper pre {
              background: #f5f5f5 !important;
              color: black !important;
              border: 1px solid #ddd !important;
            }
            
            .pdf-content-wrapper blockquote {
              background: #f9f9f9 !important;
              border-left: 4px solid #666 !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default PDFPublicView;